import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import defaultTo from 'lodash/defaultTo';
import toNumber from 'lodash/toNumber';

import {
  CostReport,
  CostReportRaw,
  CostReportSummary,
  CostReportSummaryRaw,
} from '@cast/types';
import { toBig } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

dayjs.extend(utc);
dayjs.extend(isBetween);

const CLUSTER_COST_REPORT_QUERY_KEY = 'cluster-cost-report';

export type ClusterCostReportGranularity = {
  step: number;
  unit: 'second' | 'minute' | 'hour' | 'day';
};

const granularityToSeconds = ({
  step,
  unit,
}: ClusterCostReportGranularity): number => {
  const multipliers = {
    second: 1,
    minute: 60,
    hour: 3600,
    day: 86400,
  };
  return step * multipliers[unit];
};

const getClusterCostReportQueryKey = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  stepSeconds,
}: {
  clusterId: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  stepSeconds: number;
}) =>
  [
    CLUSTER_COST_REPORT_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    useListingPrices,
    stepSeconds,
  ] as const;

const toCostNumber = (value?: string): number =>
  value ? toBig(value).toNumber() : 0;

export const enhanceCostReportItemData = (
  rawData: CostReportRaw,
  granularity: ClusterCostReportGranularity
): CostReport => ({
  timestamp: dayjs
    .utc(rawData.timestamp)
    .subtract(granularity.step, granularity.unit)
    .toISOString(),
  costOnDemand: toCostNumber(rawData.costOnDemand),
  costSpot: toCostNumber(rawData.costSpot),
  costSpotFallback: toCostNumber(rawData.costSpotFallback),
  cpuCostOnDemand: toCostNumber(rawData.cpuCostOnDemand),
  cpuCostSpot: toCostNumber(rawData.cpuCostSpot),
  cpuCostSpotFallback: toCostNumber(rawData.cpuCostSpotFallback),
  cpuCountOnDemand: Number(rawData.cpuCountOnDemand || '0'),
  cpuCountSpot: Number(rawData.cpuCountSpot || '0'),
  cpuCountSpotFallback: Number(rawData.cpuCountSpotFallback || '0'),
  ramCostOnDemand: toCostNumber(rawData.ramCostOnDemand),
  ramCostSpot: toCostNumber(rawData.ramCostSpot),
  ramCostSpotFallback: toCostNumber(rawData.ramCostSpotFallback),
  ramGibOnDemand: Number(rawData.ramGibOnDemand || '0'),
  ramGibSpot: Number(rawData.ramGibSpot || '0'),
  ramGibSpotFallback: Number(rawData.ramGibSpotFallback || '0'),
  gpuCostOnDemand: toCostNumber(rawData.gpuCostOnDemand),
  gpuCostSpot: toCostNumber(rawData.gpuCostSpot),
  gpuCostSpotFallback: toCostNumber(rawData.gpuCostSpotFallback),
  gpuCountOnDemand: Number(rawData.gpuCountOnDemand || '0'),
  gpuCountSpot: Number(rawData.gpuCountSpot || '0'),
  gpuCountSpotFallback: Number(rawData.gpuCountSpotFallback || '0'),
  tpuCostOnDemand: toCostNumber(rawData.tpuCostOnDemand),
  tpuCostSpot: toCostNumber(rawData.tpuCostSpot),
  tpuCostSpotFallback: toCostNumber(rawData.tpuCostSpotFallback),
  tpuCountOnDemand: Number(rawData.tpuCountOnDemand || '0'),
  tpuCountSpot: Number(rawData.tpuCountSpot || '0'),
  tpuCountSpotFallback: Number(rawData.tpuCountSpotFallback || '0'),
  storageCount: Number(rawData.storageGib || '0'),
  storageCost: Number(rawData.storageCost || '0'),
});

const enhanceCostReportSummaryData = (
  rawSummaryData: CostReportSummaryRaw
): CostReportSummary =>
  Object.entries(rawSummaryData).reduce(
    (result, [key, value]) => ({
      ...result,
      [key]: defaultTo(toNumber(value), 0),
    }),
    {} as CostReportSummary
  );

export type UseClusterCostReportQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  useListingPrices?: boolean;
  enabled?: boolean;
  disableRefetch?: boolean;
  granularity: ClusterCostReportGranularity;
};

export const getClusterCostReportQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices = false,
  enabled = true,
  disableRefetch,
  granularity,
}: UseClusterCostReportQueryArgs) => {
  let refetchInterval: boolean | number = false;
  if (!disableRefetch && dayjs().isBetween(startTime, endTime, null, '[]')) {
    refetchInterval = 30_000;
  }

  const stepSeconds = granularityToSeconds(granularity);

  return queryOptions({
    queryKey: getClusterCostReportQueryKey({
      clusterId,
      startTime,
      endTime,
      useListingPrices,
      stepSeconds,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getCostReport(
        {
          clusterId,
          startTime,
          endTime,
          stepSeconds,
          useListingPrices,
        },
        { timeout: 30_000 }
      );

      const items = data?.items || [];
      const summary = data?.summary || {};

      const itemsEnhanced = items.map((item) =>
        enhanceCostReportItemData(item, granularity)
      );

      const summaryEnhanced = enhanceCostReportSummaryData(summary);

      return { ...data, items: itemsEnhanced, summary: summaryEnhanced };
    },
    enabled: !!clusterId && enabled,
    refetchInterval,
  });
};

export const useClusterCostReportQuery = (
  args: UseClusterCostReportQueryArgs
) => {
  const { isLoading, data, error, refetch } = useQuery(
    getClusterCostReportQueryOptions(args)
  );

  return {
    isLoading,
    costReport: data,
    error,
    refetch,
  };
};
